import { growthChartColors } from "../../lib/chart-colors";
import UserAcquisition, { AttributionSummary, attributionSummary, acquisitionFor, channelLabel, parameterText, AcquisitionText, csvCell } from "./UserAcquisition";
import { userSearchText } from "./user-search";
import { growthAxes, metricTooltip, timeSeriesInteraction } from "../../lib/growth-chart";
import { asDate, istRangeStart, istDateKey, istISOString, formatCalendarDate } from "../../lib/admin-time";
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { useContext, useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";
import { APIContext } from "../../api";
import { Line } from "react-chartjs-2";
import "chartjs-adapter-date-fns";
import HelmetLayout from "../../components/HelmetLayout";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  TimeScale,
  Legend,
  Tooltip,
} from "chart.js";
import { PulseLoader } from "react-spinners";
import { getGravatarUrl } from "../../utils";
import DateDisplay from "../../components/ui-kit/date-display";
import {
  Users as UsersIcon,
  Download,
  RefreshCw,
  Search,
  Crown,
  TrendingUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Globe,
} from "lucide-react";

ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  TimeScale,
  Legend,
  Tooltip
);

const userRowID = (user, index) => String(user?.attributes_parsed?.sub || user?.Username || user?.username || index);

const PlanBadge = ({ plan }) => {
  const p = (plan || "free").toLowerCase();

  if (p.includes("appsumo")) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200 shadow-sm">
        <Crown size={12} className="text-amber-500 fill-amber-400" />
        {plan}
      </span>
    );
  }

  if (p.includes("pro") || p.includes("plus")) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-50 text-[#623ce7] border border-purple-200 shadow-sm">
        <Crown size={12} className="text-[#623ce7]" />
        {plan}
      </span>
    );
  }

  if (p.includes("agency") || p.includes("enterprise") || p.includes("business")) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200 shadow-sm">
        <Crown size={12} className="text-emerald-500" />
        {plan}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">
      {plan || "Free"}
    </span>
  );
};

export default function Users() {
  const [userList, setUserList] = useState([]);
  const [expandedAcquisition, setExpandedAcquisition] = useState(null);
  const attributionPanelRef = useRef(null);
  useEffect(() => {
    if (expandedAcquisition !== null) attributionPanelRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [expandedAcquisition]);
  const [userDataAnalytics, setUserDataAnalytics] = useState([]);
  const { getUserDetails, getAnalyticsData, getAvailableAddons } = useContext(APIContext);
  const [dateOffset, setDateOffset] = useState(30);

  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isRefreshingUsers, setIsRefreshingUsers] = useState(false);
  const [usersError, setUsersError] = useState("");
  const usersRequestRef = useRef(false);
  const [addonDetails, setAddonDetails] = useState({});
  const [isLoadingAddons, setIsLoadingAddons] = useState(true);

  const [planFilter, setPlanFilter] = useState([]);
  const [addonFilter, setAddonFilter] = useState([]);
  const [attributionFilter, setAttributionFilter] = useState("all");
  const [websiteFilter, setWebsiteFilter] = useState("all");
  const [accountStatusFilter, setAccountStatusFilter] = useState("all");
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });
  const [sorting, setSorting] = useState([{ id: "created", desc: true }]);
  const [columnFilters, setColumnFilters] = useState([]);

  const getStartDate = istRangeStart;

  const dateString = getStartDate(dateOffset);

  const loadUsers = async (refresh = false) => {
    if (usersRequestRef.current) return;
    usersRequestRef.current = true;
    setUsersError("");
    if (refresh) setIsRefreshingUsers(true);
    else setIsLoadingUsers(true);
    try {
      const users = await getUserDetails({ refresh });
      setUserList(Array.isArray(users) ? users : users?.users || []);
    } catch (error) {
      console.error(error);
      setUsersError(refresh
        ? "Could not refresh users. The previous list is still shown. Please try again."
        : "Could not load users. Click Refresh users to try again.");
    } finally {
      usersRequestRef.current = false;
      setIsLoadingUsers(false);
      setIsRefreshingUsers(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    setIsLoadingAddons(true);
    getAvailableAddons()
      .then((addons) => {
        const addonMap = {};
        (addons || []).forEach((addon) => {
          addonMap[addon.pk?.replace("plan-addon-", "") || addon.id] = addon;
        });
        setAddonDetails(addonMap);
      })
      .catch((error) => console.error("Failed to load addons:", error))
      .finally(() => setIsLoadingAddons(false));
  }, []);

  useEffect(() => {
    getAnalyticsData(dateString)
      .then((data) => {
        setUserDataAnalytics(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        console.error(error.message);
      });
  }, [dateOffset]);

  const paidSubscribersCount = useMemo(() => {
    return userList.filter((u) => {
      const plan = u?.subscription?.plan?.toLowerCase();
      return u?.subscription?.status === "active" && plan && plan !== "free";
    }).length;
  }, [userList]);

  const planOptions = useMemo(() => {
    const set = new Set();
    userList.forEach((u) => {
      const p = u?.subscription?.status === "active" ? u?.subscription?.plan : "Free";
      if (p) set.add(p);
    });
    return Array.from(set).sort();
  }, [userList]);

  const addonOptions = useMemo(() => {
    return Object.keys(addonDetails).map((id) => ({
      id,
      name: addonDetails[id]?.name || addonDetails[id]?.addon_name || id,
    }));
  }, [addonDetails]);

  // Chart data
  const chartLabels = userDataAnalytics.map((item) => formatCalendarDate(item.sk));
  const newUsersCounts = userDataAnalytics.map((item) => item.new_users_count);
  const totalUserCounts = userDataAnalytics.map((item) => item.user_count);

  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        label: "Total Users",
        data: totalUserCounts,
        fill: false,
        spanGaps: true,
        backgroundColor: growthChartColors.totalUsers,
        borderColor: growthChartColors.totalUsers,
        borderWidth: 2,
        tension: 0.3,
        pointRadius: 0,
      },
      {
        label: "New Signups",
        yAxisID: "daily",
        data: newUsersCounts,
        fill: false,
        spanGaps: true,
        backgroundColor: growthChartColors.newSignups,
        borderColor: growthChartColors.newSignups,
        borderWidth: 2,
        tension: 0.3,
        pointRadius: 0,
      },
    ],
  };

  const chartOptions = {
    plugins: {
      legend: {
        position: "top",
        labels: { boxWidth: 12, usePointStyle: true, font: { family: "Inter", size: 12 } },
      },
      tooltip: metricTooltip(),
    },
    interaction: timeSeriesInteraction,
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: "category",
        time: { unit: "day" },
        grid: { display: false },
        ticks: { maxRotation: 0, font: { size: 10 } },
      },
      ...growthAxes("Total users", "New signups / day", {
        totalColor: growthChartColors.totalUsers,
        dailyColor: growthChartColors.newSignups,
      }),
    },
  };

  const inArrayFilter = useCallback((row, columnId, filterValue) => {
    const v = row.getValue(columnId);
    if (Array.isArray(filterValue)) return filterValue.includes(v);
    return v === filterValue;
  }, []);

  const addonFilterFn = useCallback(
    (row, columnId, filterValue) => {
      if (!Array.isArray(filterValue) || filterValue.length === 0) return true;
      const user = row.original;
      const userAddons = user?.addons || [];
      return filterValue.some((selectedAddon) =>
        userAddons.some((userAddon) => userAddon.addonid === selectedAddon)
      );
    },
    [addonDetails]
  );

  const searchTextCache = useMemo(() => new WeakMap(), [userList, addonDetails]);
  const globalUserFilter = useCallback((row, _columnId, value) => {
    const terms = String(value ?? "").toLocaleLowerCase().trim().split(/\s+/).filter(Boolean);
    if (terms.length === 0) return true;
    let searchable = searchTextCache.get(row.original);
    if (searchable === undefined) {
      searchable = userSearchText(row.original, addonDetails);
      searchTextCache.set(row.original, searchable);
    }
    return terms.every((term) => searchable.includes(term));
  }, [addonDetails, searchTextCache]);

  const columns = useMemo(
    () => [
      {
        id: "email",
        header: "User",
        accessorFn: (user) => user?.attributes_parsed?.email || "",
        enableGlobalFilter: true,
        cell: (info) => {
          const email = info.row.original?.attributes_parsed?.email;
          const given =
            info.row.original?.attributes_parsed?.["custom:given_name"] ||
            info.row.original?.attributes_parsed?.given_name ||
            "";
          const family =
            info.row.original?.attributes_parsed?.["custom:family_name"] ||
            info.row.original?.attributes_parsed?.family_name ||
            "";
          const name = `${given} ${family}`.trim();
          const sub = info.row.original?.attributes_parsed?.sub || info.row.original?.username;

          return (
            <div className="flex items-center gap-3">
              <img
                src={getGravatarUrl(email)}
                className="w-8 h-8 rounded-full border border-slate-200 shrink-0"
                alt="user avatar"
                onError={(e) => {
                  e.target.src = "https://picsum.photos/seed/user/40/40";
                }}
              />
              <div className="min-w-0">
                <a target="_blank" rel="noopener noreferrer"
                  href={`/users/${sub}`}
                  className="font-semibold text-slate-900 hover:text-[#623ce7] transition-colors truncate block"
                >
                  {name || email || "Unknown"}
                </a>
                <p className="text-xs text-slate-400 truncate">{email}</p>
              </div>
            </div>
          );
        },
      },
      {
        id: "acquisition",
        header: "Signup attribution",
        accessorFn: (user) => JSON.stringify(acquisitionFor(user) || "Not captured"),
        enableSorting: false,
        meta: { interactive: true },
        cell: (info) => <div className="space-y-2 min-w-[160px]">
          <AttributionSummary acquisition={acquisitionFor(info.row.original)} />
          <button type="button" aria-expanded={expandedAcquisition === info.row.id} aria-controls="user-attribution-panel"
            onClick={() => setExpandedAcquisition(current => current === info.row.id ? null : info.row.id)}
            className="text-xs font-semibold text-[#623ce7] hover:underline">
            {expandedAcquisition === info.row.id ? "Hide attribution" : "View attribution"}
          </button>
        </div>,
      },
      {
        id: "source", header: "Latest known source",
        accessorFn: (user) => { const summary = attributionSummary(acquisitionFor(user)); return summary.source || channelLabel(summary.channel); },
        cell: (info) => <AcquisitionText value={info.getValue()} />,
      },
      {
        id: "landing_page", header: "Original landing page",
        accessorFn: (user) => acquisitionFor(user)?.first_touch?.landing_page || "",
        cell: (info) => <AcquisitionText value={info.getValue()} />,
      },
      {
        id: "referrer", header: "Original referrer",
        accessorFn: (user) => acquisitionFor(user)?.first_touch?.referrer || "",
        cell: (info) => <AcquisitionText value={info.getValue()} />,
      },
      {
        id: "utm", header: "Original UTMs",
        accessorFn: (user) => parameterText(acquisitionFor(user)?.first_touch?.utm),
        cell: (info) => <AcquisitionText value={info.getValue()} />,
      },
      {
        id: "plan",
        header: "Plan",
        accessorFn: (user) =>
          user?.subscription?.status === "active" ? user?.subscription?.plan || "Free" : "Free",
        filterFn: inArrayFilter,
        cell: (info) => <PlanBadge plan={info.getValue()} />,
      },
      {
        id: "websites",
        header: "Websites",
        accessorFn: (user) => (user?.projects || []).length,
        cell: (info) => {
          const count = info.getValue() || 0;
          return (
            <span
              className={`inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                count > 0
                  ? "bg-purple-50 text-[#623ce7] border border-purple-200"
                  : "bg-slate-100 text-slate-400"
              }`}
            >
              {count} {count === 1 ? "site" : "sites"}
            </span>
          );
        },
      },
      {
        id: "created",
        header: "Joined",
        accessorFn: (user) =>
          user?.UserCreateDate ? asDate(user.UserCreateDate)?.getTime() || 0 : 0,
        cell: (info) => {
          const created = info.row.original?.UserCreateDate
            ? asDate(info.row.original.UserCreateDate)
            : null;
          return created ? (
            <span className="text-slate-600 text-xs">
              <DateDisplay date={created} />
            </span>
          ) : (
            <span className="text-slate-400 text-xs">—</span>
          );
        },
      },
      {
        id: "last_login",
        header: "Last Online",
        accessorFn: (user) => {
          if (!user?.lastLoginTime) return 0;
          const t =
            typeof user.lastLoginTime === "number"
              ? user.lastLoginTime * 1000
              : asDate(user.lastLoginTime)?.getTime() || 0;
          return isNaN(t) ? 0 : t;
        },
        cell: (info) => {
          const raw = info.row.original?.lastLoginTime;
          if (!raw) return <span className="text-slate-400 text-xs">—</span>;
          const ts = typeof raw === "number" ? raw * 1000 : asDate(raw)?.getTime() || 0;
          const date = ts && !isNaN(ts) ? new Date(ts) : null;
          return date ? (
            <span className="text-slate-600 text-xs">
              <DateDisplay date={date} />
            </span>
          ) : (
            <span className="text-slate-400 text-xs">—</span>
          );
        },
      },
      {
        id: "addons",
        header: "Addons",
        accessorFn: (user) => {
          const addons = user?.addons || [];
          if (addons.length === 0) return "None";
          const addonNames = addons.map((addon) => {
            const addonDetail = addonDetails[addon.addonid];
            return addonDetail?.name || addonDetail?.addon_name || "Unknown";
          });
          return addonNames.join(", ");
        },
        filterFn: addonFilterFn,
        cell: (info) => {
          const addons = info.row.original?.addons || [];
          if (addons.length === 0) {
            return <span className="text-slate-400 text-xs">None</span>;
          }
          return (
            <div className="flex flex-wrap gap-1">
              {addons.map((a, i) => {
                const name =
                  addonDetails[a.addonid]?.name ||
                  addonDetails[a.addonid]?.addon_name ||
                  a.addonid;
                return (
                  <span
                    key={i}
                    className="px-2 py-0.5 bg-slate-100 rounded text-[11px] font-medium text-slate-600 border border-slate-200"
                  >
                    {name}
                  </span>
                );
              })}
            </div>
          );
        },
      },
    ],
    [addonDetails, inArrayFilter, addonFilterFn, expandedAcquisition]
  );

  // Pre-filter userList with website filter & account status filter
  const processedUserList = useMemo(() => {
    return userList.filter((user) => {
      if (attributionFilter !== "all" && attributionSummary(acquisitionFor(user)).status !== attributionFilter) return false;
      const siteCount = (user?.projects || []).length;
      if (websiteFilter === "has_sites" && siteCount === 0) return false;
      if (websiteFilter === "no_sites" && siteCount > 0) return false;
      if (websiteFilter === "multiple_sites" && siteCount < 2) return false;

      if (accountStatusFilter === "enabled" && user.Enabled === false) return false;
      if (accountStatusFilter === "disabled" && user.Enabled !== false) return false;

      return true;
    });
  }, [userList, websiteFilter, accountStatusFilter, attributionFilter]);

  const table = useReactTable({
    data: processedUserList,
    getRowId: userRowID,
    columns,
    state: { pagination, sorting, columnFilters, globalFilter },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: globalUserFilter,
    getColumnCanGlobalFilter: (column) => column.id === "email",
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const expandedUser = processedUserList.find((user, index) => userRowID(user, index) === expandedAcquisition);

  const filteredTotal = table.getPrePaginationRowModel().rows.length;

  const downloadTableData = () => {
    const allRows = table.getPrePaginationRowModel().rows;
    const headers = columns.map((col) => col.header).join(",");
    const csvData = allRows
      .map((row) => {
        return columns
          .map((col) => {
            let value;
            if (col.id === "created") {
              const date = row.original?.UserCreateDate
                ? asDate(row.original.UserCreateDate)
                : null;
              value = date ? istISOString(date) : "";
            } else if (col.id === "last_login") {
              const raw = row.original?.lastLoginTime;
              const ts = typeof raw === "number" ? raw * 1000 : asDate(raw)?.getTime() || 0;
              const date = ts && !isNaN(ts) ? new Date(ts) : null;
              value = date ? istISOString(date) : "";
            } else {
              value = col.accessorFn
                ? col.accessorFn(row.original)
                : row.getValue(col.id);
            }
            return csvCell(value);
          })
          .join(",");
      })
      .join("\n");

    const csvContent = `${headers}\n${csvData}`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `users_export_${istDateKey()}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const currentSortKey = sorting[0]
    ? `${sorting[0].id}-${sorting[0].desc ? "desc" : "asc"}`
    : "created-desc";

  return (
    <>
      <HelmetLayout title="Users" />
      <div className="space-y-6 pb-12">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">User Management</h1>
            <p className="text-slate-500 text-sm mt-0.5">
              Manage registered users, subscriptions, website counts, and signups trajectory.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => loadUsers(true)}
              disabled={isLoadingUsers || isRefreshingUsers}
              aria-busy={isRefreshingUsers}
              title="Fetch the latest users and update the cache"
              className="flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-slate-50 text-slate-700 rounded-lg border border-slate-200 transition-colors shadow-sm font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw size={16} className={isRefreshingUsers ? "animate-spin" : ""} />
              {isRefreshingUsers ? "Refreshing users..." : "Refresh users"}
            </button>
            <button
              onClick={downloadTableData}
              className="flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-slate-50 text-slate-700 rounded-lg border border-slate-200 transition-colors shadow-sm font-medium text-sm"
            >
              <Download size={16} /> Export CSV
            </button>
          </div>
        </div>

        {usersError && <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{usersError}</p>}

        {/* KPI StatCards (Filtered Matches REMOVED per instruction) */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">Total Users</p>
              <h3 className="text-2xl font-bold text-slate-900">
                {isLoadingUsers ? "—" : userList.length.toLocaleString()}
              </h3>
            </div>
            <div className="p-3 bg-[#623ce7]/10 text-[#623ce7] rounded-xl">
              <UsersIcon size={22} />
            </div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">Paid Subscribers</p>
              <h3 className="text-2xl font-bold text-emerald-600">
                {isLoadingUsers ? "—" : paidSubscribersCount.toLocaleString()}
              </h3>
            </div>
            <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
              <Crown size={22} />
            </div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">
                New Signups ({dateOffset}d)
              </p>
              <h3 className="text-2xl font-bold text-blue-600">
                {userDataAnalytics
                  .map((item) => item.new_users_count || 0)
                  .reduce((a, b) => parseInt(a, 10) + parseInt(b, 10), 0)
                  .toLocaleString()}
              </h3>
            </div>
            <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
              <TrendingUp size={22} />
            </div>
          </div>
        </div>

        {/* Growth Chart */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-5">
            <div>
              <h3 className="text-base font-bold text-slate-900">Signups & Growth</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Total users: left axis · New signups: right axis · Daily cutoffs in IST.
              </p>
            </div>
            <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
              {[7, 15, 30, 60].map((days) => (
                <button
                  key={days}
                  onClick={() => setDateOffset(days)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    dateOffset === days
                      ? "bg-white text-slate-900 shadow-sm font-semibold"
                      : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  {days}d
                </button>
              ))}
            </div>
          </div>
          <div className="h-[280px] w-full">
            <Line data={chartData} options={chartOptions} />
          </div>
        </div>

        {/* Users Table Card with Enhanced Filters & Sorting */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Controls Bar */}
          <div className="p-4 border-b border-slate-200 flex flex-col gap-4 bg-slate-50/50">
            <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
              {/* Global Search */}
              <div className="relative flex-1 max-w-md">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  size={17}
                />
                <input
                  type="text"
                  placeholder="Search users by name, email, ID, plan, add-on, attribution..."
                  value={globalFilter ?? ""}
                  onChange={(e) => {
                    setGlobalFilter(e.target.value);
                    table.setPageIndex(0);
                  }}
                  className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-[#623ce7] focus:outline-none transition-all"
                />
              </div>

              {/* Quick Sort Dropdown */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 font-semibold whitespace-nowrap flex items-center gap-1">
                  <ArrowUpDown size={14} /> Sort:
                </span>
                <select
                  className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 outline-none"
                  value={currentSortKey}
                  onChange={(e) => {
                    const [id, dir] = e.target.value.split("-");
                    setSorting([{ id, desc: dir === "desc" }]);
                    table.setPageIndex(0);
                  }}
                >
                  <option value="created-desc">Joined: Newest First</option>
                  <option value="created-asc">Joined: Oldest First</option>
                  <option value="last_login-desc">Last Online: Recent First</option>
                  <option value="last_login-asc">Last Online: Oldest First</option>
                  <option value="websites-desc">Websites: Most First</option>
                  <option value="websites-asc">Websites: Fewest First</option>
                  <option value="plan-asc">Plan: A to Z</option>
                  <option value="plan-desc">Plan: Z to A</option>
                </select>
              </div>
            </div>

            {/* Filter Pills / Dropdowns Row */}
            <div className="flex items-center gap-3 flex-wrap">
              <label className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
                Attribution:
                <select aria-label="Attribution" value={attributionFilter} onChange={(event) => { setAttributionFilter(event.target.value); table.setPageIndex(0); }} className="bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-700 outline-none">
                  <option value="all">All sources</option>
                  <option value="known">Known source</option>
                  <option value="unknown">Source unavailable</option>
                  <option value="missing">No record captured</option>
                </select>
              </label>
              {/* Plan Filter */}
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-slate-500 font-medium">Plan:</span>
                <select
                  className="bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-700 outline-none"
                  value={planFilter[0] || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (!val) {
                      setPlanFilter([]);
                      setColumnFilters((prev) => prev.filter((f) => f.id !== "plan"));
                    } else {
                      setPlanFilter([val]);
                      setColumnFilters((prev) => [
                        ...prev.filter((f) => f.id !== "plan"),
                        { id: "plan", value: [val] },
                      ]);
                    }
                    table.setPageIndex(0);
                  }}
                >
                  <option value="">All Plans</option>
                  {planOptions.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>

              {/* Websites Created Filter */}
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-slate-500 font-medium">Websites:</span>
                <select
                  className="bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-700 outline-none"
                  value={websiteFilter}
                  onChange={(e) => {
                    setWebsiteFilter(e.target.value);
                    table.setPageIndex(0);
                  }}
                >
                  <option value="all">All Counts</option>
                  <option value="has_sites">Has Sites (&gt; 0)</option>
                  <option value="no_sites">No Sites (0)</option>
                  <option value="multiple_sites">Multiple Sites (2+)</option>
                </select>
              </div>

              {/* Account Status Filter */}
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-slate-500 font-medium">Status:</span>
                <select
                  className="bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-700 outline-none"
                  value={accountStatusFilter}
                  onChange={(e) => {
                    setAccountStatusFilter(e.target.value);
                    table.setPageIndex(0);
                  }}
                >
                  <option value="all">All Accounts</option>
                  <option value="enabled">Active / Enabled</option>
                  <option value="disabled">Disabled</option>
                </select>
              </div>

              {/* Addons Filter */}
              {addonOptions.length > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-slate-500 font-medium">Addons:</span>
                  <select
                    className="bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-700 outline-none"
                    value={addonFilter[0] || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!val) {
                        setAddonFilter([]);
                        setColumnFilters((prev) => prev.filter((f) => f.id !== "addons"));
                      } else {
                        setAddonFilter([val]);
                        setColumnFilters((prev) => [
                          ...prev.filter((f) => f.id !== "addons"),
                          { id: "addons", value: [val] },
                        ]);
                      }
                      table.setPageIndex(0);
                    }}
                  >
                    <option value="">All Addons</option>
                    {addonOptions.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Rows Per Page */}
              <div className="flex items-center gap-1.5 ml-auto">
                <span className="text-xs text-slate-500 font-medium">Rows:</span>
                <select
                  className="bg-white border border-slate-200 rounded-lg px-2 py-1 text-xs text-slate-700 outline-none"
                  value={table.getState().pagination.pageSize}
                  onChange={(e) => {
                    table.setPageSize(parseInt(e.target.value, 10));
                    table.setPageIndex(0);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          </div>

          {expandedUser && <div id="user-attribution-panel" ref={attributionPanelRef} className="p-4 bg-slate-50 border-b border-slate-200">
            <div className="flex items-center justify-between gap-4 mb-3">
              <p className="text-sm font-semibold text-slate-700 break-all">Attribution for {expandedUser.attributes_parsed?.email || expandedUser.Username || expandedUser.username}</p>
              <button type="button" onClick={() => setExpandedAcquisition(null)} className="text-sm font-semibold text-[#623ce7] shrink-0">Close attribution</button>
            </div>
            <UserAcquisition acquisition={acquisitionFor(expandedUser)} />
          </div>}

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider border-b border-slate-200 select-none"
                  >
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        onClick={header.column.getToggleSortingHandler()}
                        className="p-4 font-semibold cursor-pointer hover:bg-slate-100 transition-colors"
                      >
                        <div className="flex items-center gap-1.5">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          <span className="text-[10px] text-slate-400">
                            {header.column.getIsSorted() === "asc"
                              ? "▲"
                              : header.column.getIsSorted() === "desc"
                              ? "▼"
                              : ""}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {isLoadingUsers ? (
                  <tr>
                    <td colSpan={columns.length} className="p-12 text-center">
                      <PulseLoader color="#623ce7" size={10} />
                      <p className="text-xs text-slate-400 mt-2">Loading users...</p>
                    </td>
                  </tr>
                ) : table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="p-12 text-center text-slate-400"
                    >
                      No users match the selected filters.
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      className="hover:bg-slate-50/80 transition-colors cursor-pointer"

                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="p-4 whitespace-nowrap">
                          {cell.column.id === "email" || cell.column.columnDef.meta?.interactive ? flexRender(cell.column.columnDef.cell, cell.getContext()) : <a href={`/users/${encodeURIComponent(row.original?.attributes_parsed?.sub || row.original?.username)}`} target="_blank" rel="noopener noreferrer" className="block hover:text-[#623ce7]">{flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}</a>}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          <div className="p-4 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4 bg-slate-50/30 text-xs text-slate-500">
            <div>
              Showing{" "}
              <strong className="text-slate-700 font-semibold">
                {filteredTotal === 0
                  ? 0
                  : pagination.pageIndex * pagination.pageSize + 1}
              </strong>{" "}
              to{" "}
              <strong className="text-slate-700 font-semibold">
                {Math.min(
                  (pagination.pageIndex + 1) * pagination.pageSize,
                  filteredTotal
                )}
              </strong>{" "}
              of{" "}
              <strong className="text-slate-700 font-semibold">
                {filteredTotal}
              </strong>{" "}
              users
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="text-slate-600 font-medium px-2">
                Page {table.getState().pagination.pageIndex + 1} of{" "}
                {table.getPageCount() || 1}
              </span>
              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
